/**
 * What the project record's desktop layout shares with its tabs and sections: the values
 * `ProjectDetailView` already computed (no second fetch), the user's permissions and the tab in
 * the URL.
 */
import type { Event, ProjectEvent } from '@kalos/kalos-rpc';
import { createContext, createElement, type ReactNode, useContext } from 'react';

import type { useUnityBuildIntegration } from '../../../hooks';
import type { ProjectBudgetSummary, ProjectDepartmentAssignment } from '../../../types';

/**
 * The record's one row of tabs, in order, as they appear in the `desktopTab` search param.
 * Overview, Departments, Invoices and Progress are the money tabs.
 */
export const PROJECT_DESKTOP_TABS = [
  'overview',
  'departments',
  'invoices',
  'change-orders',
  'progress',
  'notes',
  'activity',
] as const;
export type ProjectDesktopTab = (typeof PROJECT_DESKTOP_TABS)[number];

/**
 * The tabs a user can see, with today's gates: the money tabs and Change orders and Activity
 * need budget permission, except Invoices, which needs invoice permission (106) only. Departments
 * and Notes are for everyone.
 */
export const visibleDesktopTabs = ({
  canViewBudgets,
  canManageProjectInvoices,
}: Pick<ProjectDesktopPermissions, 'canViewBudgets' | 'canManageProjectInvoices'>) =>
  PROJECT_DESKTOP_TABS.filter((tab) => {
    if (tab === 'departments' || tab === 'notes') return true;
    if (tab === 'invoices') return canManageProjectInvoices;
    return canViewBudgets;
  });

/** The Unity Build contract figures, or null when the project isn't connected. */
export type ProjectContractData = ReturnType<typeof useUnityBuildIntegration>['contractData'];

/**
 * Where the budget comes from, by the view's own data: a loaded Unity Build contract, the
 * project's own budget with data, or neither. A summary for labels only; the budget controls decide
 * their modes (setup choice, disconnected) with `ManageBudgetSection`'s rule.
 */
export type ProjectBudgetSource = 'unity' | 'manual' | 'none';

/** A job as the existing sections take it (`projectEvents` / `projectEventOptions`). */
export type ProjectEventOption = Pick<Event, 'id' | 'description' | 'jobSubtype'>;

export type ProjectDesktopPermissions = {
  /** Overview, Departments' budgets, Progress, Change orders and Activity. */
  canViewBudgets: boolean;
  /** Invoices (permission 106). */
  canManageProjectInvoices: boolean;
  /** Connect / disconnect Unity Build. */
  canAccessUnityBuild: boolean;
  /** Cost report links on jobs (`CostReportAccess` group). */
  canViewCostReport: boolean;
};

export type ProjectDesktopContextValue = {
  /** The event id from the URL (`/project/:projectId`), the primary job's id. */
  projectId: number;
  /** The primary job's event. */
  project: Event;
  projectEvent?: ProjectEvent;
  /**
   * Exactly the view's `actualProjectId` (`project.parentId || projectEvent.id || 0`): what today's
   * budget, estimate, change order and Field % complete panels take as `projectId`.
   */
  actualProjectId: number;
  /**
   * Exactly what the view passes to `InvoicesTab` as `actualProjectId` today
   * (`project.parentId || 0`, no `projectEvent.id` fallback). Use this one for invoices.
   */
  invoicesProjectId: number;
  /** The primary job's event id (`projectEvent.primaryEventId`, else `projectId`). */
  primaryEventId: number;
  /** Child jobs. */
  jobs: Event[];
  /**
   * True on the record's first load, until the jobs (and the view's other loads) arrive. The
   * project shows first; budget panels read as loading meanwhile instead of "no budget yet".
   */
  isLoadingRecord: boolean;
  /** `jobs` reduced to what the existing sections take as `projectEvents`. */
  projectEventOptions: ProjectEventOption[];
  departmentAssignments: ProjectDepartmentAssignment[];
  /** The project's own budget summary, as fetched. */
  budgetSummary?: ProjectBudgetSummary;
  /**
   * The budget summary the Budget Overview shows: Unity Build's contract budget with our actuals
   * when connected, else the project's own summary when it has data, else undefined.
   */
  effectiveBudgetSummary?: ProjectBudgetSummary;
  /** True when the project's own budget has meaningful budget or actual data. */
  hasProjectMgmtData: boolean;
  contractData: ProjectContractData;
  isUnityBuildProject: boolean;
  budgetSource: ProjectBudgetSource;
  /** As the view passes `InvoicesTab` today: the project's customer, else the first job's. */
  invoiceCustomer: Event['customer'];
  /** As the view passes `InvoicesTab` today: `projectEvent.customerName || 'Customer'`. */
  invoiceCustomerName: string;
  permissions: ProjectDesktopPermissions;
  /** The tab showing (always one the user can see). */
  tab: ProjectDesktopTab;
  /** The tabs this user can see, in order (`visibleDesktopTabs`). */
  tabs: ProjectDesktopTab[];
  /** Show a tab. Replaces the history entry. */
  setTab: (tab: ProjectDesktopTab) => void;
  /**
   * True while the details card holds unsaved typing (a text field or the departments editor is
   * open with changes). Leaving the tab asks first.
   */
  isEditingDetails: boolean;
  setIsEditingDetails: (isEditing: boolean) => void;
  /** Shows Departments with this job's department opened (a row on Overview's Departments). */
  openDepartment: (eventId: number) => void;
  /** The job `openDepartment` asked for, until Departments has opened and shown it. */
  departmentToOpen: number | null;
  clearDepartmentToOpen: () => void;
};

const ProjectDesktopContext = createContext<ProjectDesktopContextValue | null>(null);

/** Gives the record's tabs their values; `ProjectRecordDesktop` is the only place that renders it. */
export const ProjectDesktopProvider = ({
  value,
  children,
}: {
  value: ProjectDesktopContextValue;
  children: ReactNode;
}) => createElement(ProjectDesktopContext.Provider, { value }, children);

/** The project record's desktop values. Only inside `ProjectRecordDesktop`. */
export const useProjectDesktop = (): ProjectDesktopContextValue => {
  const value = useContext(ProjectDesktopContext);
  if (!value) throw new Error('useProjectDesktop must be used inside ProjectRecordDesktop');
  return value;
};
